import { combineReducers } from 'redux'
import { createTimeframedState } from 'reducers/globalReducer'
import { MoneyflowDataState, MoneyflowMemberTrendsByCategory, MoneyflowBreakdownCollection, FullMoneyflowType } from 'interfaces/moneyflow'
import { createEmptyCollection, EMPTY_COLLECTION, removeByKeyFromCollection } from 'helpers/normalizeHelpers'
import { Timeframe } from 'interfaces/chart'
import {
  SET_TRANSACTION,
  SET_TRANSACTIONS,
  SET_MEMBER_MONEYFLOW_TRENDS,
  SET_MEMBER_MONEYFLOW_BREAKDOWN,
  GET_MONEYFLOW_TRACKERS,
  APPEND_MONEYFLOW_TRACKER,
  DELETE_MONEYFLOW_TRACKER,
  CHANGE_TRANSACTION_CATEGORY,
} from 'actions/moneyflowActions'
import { setItemInCollection } from 'helpers/normalizeHelpers'
import { Reducer } from 'interfaces'
import { isSuccessAction, isRequestAction } from 'helpers/actionsHelpers'
import {
  needDiscardData,
  createMoneyflowTrendDiscardTriggers,
  createTransactionDiscardTriggers,
  createMoneyflowBreakdownDiscardTriggers,
} from 'helpers/discardTriggerHelpers'
import { processChangeTransactionCategory } from 'helpers/moneyflowHelpers'

// ===========================================================================//
//                                  BREAKDOWN                                 //
// ===========================================================================//


const createBreakdownReducer = (type: FullMoneyflowType) => {
  const triggers = createMoneyflowBreakdownDiscardTriggers(type)

  return (timeframe: Timeframe): Reducer<MoneyflowBreakdownCollection> =>
    (state = EMPTY_COLLECTION, action) => {
      if (needDiscardData(triggers)(action)) {
        return EMPTY_COLLECTION
      }

      if (action.type === SET_MEMBER_MONEYFLOW_BREAKDOWN &&
        action.payload.type === type &&
        action.payload.timeframe === timeframe
      ) {
        return action.payload.data
      }

      return state
    }
}

const createBreakdownsReducer = (type: FullMoneyflowType) => {
  const creator = createBreakdownReducer(type)

  return createTimeframedState(creator)
}


// ===========================================================================//
//                                    TRENDS                                  //
// ===========================================================================//

const createTrendsByCategoryReducer = (type: FullMoneyflowType) => {
  const triggers = createMoneyflowTrendDiscardTriggers(type)

  return (timeframe: Timeframe): Reducer<MoneyflowMemberTrendsByCategory> =>
    (state = {}, action) => {
      if (needDiscardData(triggers)(action)) {
        return {}
      }

      if (action.type === SET_MEMBER_MONEYFLOW_TRENDS &&
          action.payload.type === type &&
          action.payload.timeframe === timeframe
      ) {
        return { ...state, [action.payload.category]: action.payload.data }
      }

      return state
    }
}

const createTrendsReducer = (type: FullMoneyflowType) => {
  const creator = createTrendsByCategoryReducer(type)

  return createTimeframedState(creator)
}


// ===========================================================================//
//                                TRANSACTIONS                                //
// ===========================================================================//

const createTransactionsReducer = (type: FullMoneyflowType): Reducer<MoneyflowDataState['transactions']> => {
  const triggers = createTransactionDiscardTriggers(type)

  return (prevState = null, action) => {
    if (needDiscardData(triggers)(action)) {
      return null
    }

    switch (action.type) {
      case SET_TRANSACTIONS:
        return action.payload.type === type ? action.payload.transactions : prevState

      case CHANGE_TRANSACTION_CATEGORY: {
        if (prevState === null) {
          return prevState
        }

        const nextCollection = processChangeTransactionCategory({
          type,
          prevCollection: prevState.results,
          action,
        })

        return {
          ...prevState,
          results: nextCollection,
        }
      }

      case SET_TRANSACTION: {
        const { transaction: nextTransaction } = action.payload
        const shouldUpdate = nextTransaction.category.type === type || type === 'transactions'
        if (shouldUpdate) {
          const count = prevState ? prevState.count : 0
          const normalizedState = prevState || {
            count,
            results: createEmptyCollection(),
            next: '',
            previous: '',
          }

          const isExistingTransaction = Boolean(normalizedState.results.byKey[nextTransaction.uid])
          return {
            ...normalizedState,
            count: isExistingTransaction ? count : count + 1,
            results: setItemInCollection(normalizedState.results, nextTransaction, 'uid', { mergeIfExist: true }),
          }
        }

        return prevState
      }

      default:
        return prevState
    }
  }
}


// ===========================================================================//
//                                  TRACKERS                                  //
// ===========================================================================//

const createTrackersReducer = (moneyflowType: FullMoneyflowType): Reducer<MoneyflowDataState['trackers']> =>
  (state = null, action) => {
    if (
      action.type === GET_MONEYFLOW_TRACKERS
      && isSuccessAction(action)
      && action.payload.params.type === moneyflowType
    ) {
      return action.payload.response
    }

    if (action.type === APPEND_MONEYFLOW_TRACKER && action.payload.type === moneyflowType) {
      return setItemInCollection(state || EMPTY_COLLECTION, action.payload.data, 'category')
    }

    if (
      action.type === DELETE_MONEYFLOW_TRACKER
      && isRequestAction(action)
      && action.payload.params.type === moneyflowType
    ) {
      return removeByKeyFromCollection(state || EMPTY_COLLECTION, action.payload.params.tracker.category)
    }

    return state
  }

export const spendingData = combineReducers<MoneyflowDataState>({
  breakdown: createBreakdownsReducer('spending'),
  trends: createTrendsReducer('spending'),
  trackers: createTrackersReducer('spending'),
  transactions: createTransactionsReducer('spending'),
})

export const incomeData = combineReducers<MoneyflowDataState>({
  breakdown: createBreakdownsReducer('income'),
  trends: createTrendsReducer('income'),
  trackers: createTrackersReducer('income'),
  transactions: createTransactionsReducer('income'),
})

export const transactionsData = combineReducers<Pick<MoneyflowDataState, 'transactions'>>({
  transactions: createTransactionsReducer('transactions'),
})
